import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { submitForm } from '@/features/games/launchGame.js';
import { GameRestrictionNotice } from '@/features/promo/GameRestrictionNotice.js';
import { BrandLogo } from '@/components/layout/BrandLogo.js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import { toggleChatwootBubble } from '@/lib/chatwoot/index.js';
import type { LockableCard } from '@/features/promo/use-game-lock.js';
import type { LaunchMethod } from '@/features/games/api.js';

interface GameIframeModalProps {
  url: string;
  method: LaunchMethod;
  payload: Record<string, unknown> | null;
  /** Optional game name to show in the header. */
  title?: string;
  /**
   * The catalog identity of what is running in the frame, so a session that was
   * already open when a bonus started restricting this camp can say so (#546).
   */
  game?: LockableCard | null;
  onClose: () => void;
}

const IFRAME_NAME = 'bf-game-iframe';

export function GameIframeModal({ url, method, payload, title, game, onClose }: GameIframeModalProps) {
  const { t } = useTranslation(['lobby', 'common']);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Hide the support bubble for the duration of the modal.
  useEffect(() => {
    toggleChatwootBubble('hide');
    return () => {
      toggleChatwootBubble('show');
    };
  }, []);

  // For POST launches, build a hidden form targeting the iframe by name and
  // submit once after the iframe is mounted. For GET we rely on the `src`
  // attribute directly.
  useEffect(() => {
    if (method !== 'POST' || !iframeRef.current) return;
    submitForm(url, payload ?? {}, IFRAME_NAME);
  }, [method, url, payload]);

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        hideClose
        aria-label={title ?? t('lobby:gameLaunch.modalLabel')}
        className="max-w-3xl h-[85dvh] flex flex-col gap-0 p-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">{title ?? t('lobby:gameLaunch.title')}</DialogTitle>
        {/* Header */}
        <header className="shrink-0 h-12 flex items-center justify-between px-4 border-b border-[var(--border)] bg-[var(--bg-raised)]">
          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo markOnly size={24} className="shrink-0" />
            <span className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
              {title ?? t('lobby:gameLaunch.title')}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:common.close')}
            className="w-9 h-9 rounded-full grid place-items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] transition-colors"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>

        <GameRestrictionNotice game={game} />

        {/* Game iframe */}
        <iframe
          ref={iframeRef}
          name={IFRAME_NAME}
          // For POST we still set src to about:blank so the iframe has a
          // navigable document before the form submission targets it.
          src={method === 'GET' ? url : 'about:blank'}
          title={title ?? t('lobby:gameLaunch.title')}
          className="flex-1 min-h-0 w-full border-0 bg-black"
          allow="autoplay; fullscreen; payment"
          allowFullScreen
        />
      </DialogContent>
    </Dialog>
  );
}
